import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Grid from '@mui/material/Grid';
import { STEPS } from 'constants/productVariantImport';
import Button from 'components/UI/Button';
import SearchBar from '../../UI/Search';
import Vendor from '../../../data/Vendor';
import ProductListingFilters, {
  ProductListFilter,
} from './ProductListingFilters';
import { CommissionColumnUS, CommissionColumnCA } from './ProductListingColumns';
import { renderToast, toast } from '../../UI/ToastNotification';
import { navigate } from 'hooks/useHookRouterCompat';
import ProductListingTable from './ProductListingTable';
import ProductColumns from './ProductListingColumns';
import { getLocalItem, save, clear } from '../../../lib/LocalStorage';
import ProductLocalization from '../ProductLocalization';
import { Main } from 'styles/themeColors';
import FlexboxContainer from '../../UI/FlexboxContainer';
import Tooltip from 'components/UI/Tooltip';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { useAuth } from 'lib/Auth';
import * as Prod from 'data/Product';
import { postFile, useAPI } from 'lib/API';
import { isBrandLevelRoles, isOfmpAdmin } from 'data/User';
import { PRODUCT_COLUMNS_STORAGE_KEY, PRODUCT_CONSTANTS, PRODUCT_STATUS, VALIDATION_STATUS } from 'constants/product';
import { getItems } from 'lib/SessionStorage';
import { capitalizeFirstLetter, formatDateTime } from 'utils/formatters';
import { BusinessUnit } from 'data/BusinessUnit';
import ImportModal from 'components/UI/ImportModal';
import Icon from 'view/Icon';
import { useReadPermission } from 'hooks/useUserRoles';
import { STORAGE_KEYS, GENERAL_CONSTANTS } from 'constants/general';
import FeatureFlag from 'data/FeatureFlag';
import { validateProductCreation } from 'utils/productHelpers';
import StyledBadge from 'components/UI/Badge';
import { PRODUCT_TYPE } from 'constants/product';
import { getProductTypeConfig } from 'utils/product';
import { isProductStatusPublished, PRODUCT_TYPE_CONFIG } from 'utils/product';
import { BUSINESS_UNIT_RELATIONS } from 'constants/credit';

const TooltipContent = styled.div`
  text-align: left;
  white-space: initial;
  width: 15rem;
`;

const ProductBrochureIcon = styled(Icon).attrs({ size: 20 }) <any>`
  margin-right: 0 !important;
`;

const ContainerGrid = muiStyled(Grid)({
  marginTop: '20px',
  width: '100%',
  padding: '5px',
});

const GridPadding = muiStyled(Grid)({
  padding: '0 20px',
});

const GridMargin = muiStyled(Grid)({
  margin: '0 20px',
});

interface Props {
  selectedVendor: Vendor;
  vendorsList: Vendor[];
  selectedBusinessUnit: BusinessUnit;
  featureFlags: FeatureFlag;
  productType?: string;
}

const ProductListV2 = ({ selectedVendor, vendorsList, selectedBusinessUnit, featureFlags, productType = PRODUCT_TYPE.REGULAR }: Props) => {
  const typeConfig = getProductTypeConfig(productType);
  const queryParameters = new URLSearchParams(window.location.search);
  const status = queryParameters.get("status");
  const publishStatuses = getItems(STORAGE_KEYS.PRODUCT_STATUSES);

  const { user, token } = useAuth();

  const isAdmin = isOfmpAdmin(user!);


  const baseColumns = useMemo(() => {
    if (!typeConfig.showCommissionColumn) return ProductColumns;
    const cols = [...ProductColumns];
    cols.splice(cols.length - 1, 0, CommissionColumnUS, CommissionColumnCA);
    return cols;
  }, [typeConfig.showCommissionColumn]);

  useEffect(() => {
    const savedProductColumns = getLocalItem(PRODUCT_COLUMNS_STORAGE_KEY);
    if (savedProductColumns.length === 0) {
      save(PRODUCT_COLUMNS_STORAGE_KEY, ProductColumns);
    } else {
      let copyLocalColumns = ProductColumns.map((x: any) => {
        if (x.title === 'Publish Date') {
          return {
            ...x,
            render: (rowData: any) =>
              rowData.publishedOn ? formatDateTime(rowData.publishedOn, true) : 'N/A',
          }
        }
        return x;
      })
      setLocalProductColumns(copyLocalColumns);
    }

    try {
      clear('productColumns');
    } catch (err: any) {
      console.log(err?.message);
    }
  }, [])

  const [localProductColumns, setLocalProductColumns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState(localProductColumns.length === 0 ? baseColumns : localProductColumns);
  const [searchTerm, setSearchTerm] = useState('');
  const isBrandUser = isBrandLevelRoles(user!);

  // Memoize businessUnits to maintain stable reference and prevent unnecessary re-renders
  const businessUnits = useMemo(() => getItems(STORAGE_KEYS.BUSINESS_UNITS), [selectedVendor?.id]);

  // Memoize vendorBusinessUnit to avoid recalculation on every render
  const vendorBusinessUnit = useMemo(() =>
    businessUnits.filter((bu: BusinessUnit) => bu.vendorId === selectedVendor?.id) || [],
    [businessUnits, selectedVendor?.id]
  );

  // Memoize brand filter initialization with stable dependencies
  const initialBrandFilter = useMemo(() => {
    if (selectedBusinessUnit?.brand) {
      return [{ value: selectedBusinessUnit.brand.name, id: selectedBusinessUnit.brand.id.toString() }];
    }
    return vendorBusinessUnit
      .map((bu: BusinessUnit) => bu.brand ? { value: bu.brand.name, id: bu.brand.id.toString() } : undefined)
      .filter((b: any) => b);
  }, [selectedBusinessUnit?.brand?.id, selectedBusinessUnit?.brand?.name, vendorBusinessUnit]);

  // Single filter state
  const [filters, setFilters] = useState<ProductListFilter>({
    publishStatus: { value: GENERAL_CONSTANTS.ALL },
    brands: initialBrandFilter,
    vendor: { value: ProductLocalization.listing.filter.vendorSelect },
    country: { value: ProductLocalization.listing.filter.countrySelect },
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState<'none' | 'update' | 'revise'>('none');
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [reload, setReload] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [step, setStep] = useState(1);
  const [uploadingStatus, setUploadingStatus] = useState<any>(null);
  const [fileData, setFileData] = useState<any>(null);

  const [genAIProductId, setGenAIProductId] = useState<number | null>(null);

  const { readOnly: goLiveReadOnly } = useReadPermission(typeConfig.permissions.goLive);

  const [vendorImportStates, setVendorImportStates] = useState<Record<number, any>>({});

  const updateVendorImportState = (vendorId: number, newState: any) => {
    setVendorImportStates(prev => ({
      ...prev,
      [vendorId]: {
        ...prev[vendorId],
        ...newState,
      }
    }));
    setStep(newState.step);
  };

  useEffect(() => {
    if (step === STEPS.COMPLETION) {
      setReload(true);
    }
  }, [step])

  const prodExportReq = useAPI({
    deferFn: Prod.exportProducts,
    onResolve: result => {
      let type = toast.TYPE.INFO;
      let message = `Export of ${selectedRows.length} products started...`

      if (result.status === VALIDATION_STATUS.PARTIAL) {
        type = toast.TYPE.WARNING;
        message = `${ProductLocalization.warning.exportMaterialApi}. Export of ${result?.productIds?.length || 0} products started...`;
      } else if (result.status === VALIDATION_STATUS.FAILED) {
        type = toast.TYPE.ERROR;
        message = ProductLocalization.errors.exportMaterialApi;
      }

      renderToast(type, message);
      setSelectedRows(() => []);
      setReload(true);
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  // Memoize callback for filter updates
  const handleFilterUpdate = useCallback((newFilters: ProductListFilter) => {
    setFilters(newFilters);
  }, []);

  const publishProducts = () => {
    if (selectedRows.every(row => row.status === PRODUCT_STATUS.READY)) {
      setShowPublishModal(true);
    } else {
      renderToast(toast.TYPE.ERROR, productType === PRODUCT_TYPE.SUBSCRIPTION ? ProductLocalization.errors.publishSubscription : ProductLocalization.errors.publish);
    }
  };

  const showBrochureModal = (state: boolean) => {
    setShowImportModal(state);
    const vendorState = vendorImportStates[selectedVendor.id]?.step
    const passed = vendorImportStates[selectedVendor.id]?.uploadingStatus?.passedCount == 1 ? true : false

    if (vendorState === 3 && passed) {
      setVendorImportStates(prev => {
        const copy = { ...prev };
        delete copy[selectedVendor.id];
        return copy;
      });
    }
  }

  const goLiveProducts = async () => {
    if (!selectedRows.every(row => isProductStatusPublished(row.status))) {
      renderToast(toast.TYPE.ERROR, ProductLocalization.errors.live);
      return;
    }

    prodExportReq.run(selectedRows.map((row) => row.id));
  }

  const onDelete = () => {

    const hasPublishedProducts = selectedRows.filter((row: any) => row.status === PRODUCT_STATUS.PUBLISHED).length > 0;
    const hasReadyProducts = selectedRows.filter((row: any) => row.status === PRODUCT_STATUS.READY).length > 0;
    if (hasPublishedProducts || hasReadyProducts) {
      renderToast(toast.TYPE.ERROR, productType === PRODUCT_TYPE.SUBSCRIPTION ? ProductLocalization.errors.deleteSubscription : ProductLocalization.errors.delete);
      return;
    }
    setShowDeleteModal(true)
  }

  // Update filters when URL status parameter changes
  useEffect(() => {
    if (status && publishStatuses) {
      const publishStatus = publishStatuses.find((x: any) => x.name === status);
      if (publishStatus) {
        setFilters(prev => ({
          ...prev,
          publishStatus: { ...publishStatus, value: capitalizeFirstLetter(publishStatus.name!) }
        }));
      }
    }
  }, [status]);

  const handleUpload = async () => {
    try {
      const brandId = selectedBusinessUnit?.brand?.id;
      const response = await postFile('v2/products/brochure', fileData, token!, JSON.stringify({ vendorId: selectedVendor?.id, brandId }));
      if (response.productId) {
        setGenAIProductId(response.productId);
      }
    } catch (err: any) {
      setStep(3);
      setUploadingStatus({ totalCount: 1, passedCount: 0 });
      renderToast(toast.TYPE.ERROR, err?.message);
    }
  }

  const handleViewResult = () => {
    navigate(`${getProductTypeConfig(productType).detailUrl}${genAIProductId}`);
  }

  // Update brand filter when selectedBusinessUnit changes - use a ref to track if it's the initial mount
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip the initial mount to avoid duplicate call
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const brandFilter = selectedBusinessUnit?.brand
      ? [{ value: selectedBusinessUnit.brand.name, id: selectedBusinessUnit.brand.id.toString() }]
      : vendorBusinessUnit
        .map((bu: BusinessUnit) => bu.brand ? { value: bu.brand.name, id: bu.brand.id.toString() } : undefined)
        .filter((b: any) => b);

    // Use functional update to avoid reading from potentially stale filters
    setFilters(prev => {
      // Compare with previous state to avoid unnecessary updates
      const hasChanged = JSON.stringify(prev.brands) !== JSON.stringify(brandFilter);

      if (!hasChanged) {
        return prev; // Return same reference to prevent re-renders
      }

      return {
        ...prev,
        brands: brandFilter
      };
    });
  }, [selectedBusinessUnit?.brand?.id, vendorBusinessUnit]);

  // Memoize selected brands for filter component
  const selectedBrands = useMemo(() => {
    if (selectedBusinessUnit?.brand) {
      return [selectedBusinessUnit.brand];
    }
    return vendorBusinessUnit.map((bu: BusinessUnit) => bu.brand).filter((b: any) => b);
  }, [selectedBusinessUnit?.brand?.id, vendorBusinessUnit]);

  const handleCloseImportModal = () => {
    showBrochureModal(false);

    //if product is created, reset the product id
    if (genAIProductId) {
      setGenAIProductId(null);
    }
  }
  if ((productType === PRODUCT_TYPE.SUBSCRIPTION || productType === PRODUCT_TYPE.AFFILIATE) && !selectedBusinessUnit) {
    return <ContainerGrid container><span style={{ margin: '0px 20px', fontSize: '24px' }}>{BUSINESS_UNIT_RELATIONS.PLEASE_SELECT_BUSINESS_UNIT}</span></ContainerGrid>;
  }

  if (typeConfig.requiresVendorSelection && isAdmin && !selectedVendor) {
    return <ContainerGrid container><span style={{ margin: '0px 20px', fontSize: '24px' }}>{BUSINESS_UNIT_RELATIONS.PLEASE_SELECT_VENDOR}</span></ContainerGrid>;
  }

  return (
    <ContainerGrid container>
      <GridPadding container sx={{ width: '100%' }}>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <SearchBar
            placeholder={ProductLocalization.listing.filter.search}
            searchValue={searchTerm}
            onSearch={(searchText: string) => {
              setSearchTerm(searchText);
            }}
          />
        </Grid>

        <Grid
          size={{ xs: 12, sm: 8, md: 8 }}
          container
          justifyContent="flex-end"
          alignItems="center"
        >

          {!typeConfig.hideActionButtons && <>
            {(featureFlags?.businessUnit?.enabled || isAdmin) &&
              <Tooltip
                content={
                  <TooltipContent>
                    {PRODUCT_CONSTANTS.PUSH_SELECTED_ON_LIVE}
                  </TooltipContent>
                }
                key="submit"
                size="large"
                hidden={goLiveReadOnly}
              >
                <Button
                  onClick={goLiveProducts}
                  margin="0 10px 0 0"
                  bgColor={Main.brandDark}
                  borderColor={Main.brandDark}
                  disabled={selectedRows.length === 0}
                  permissionCode={typeConfig.permissions.goLive}
                >
                  {PRODUCT_CONSTANTS.GO_LIVE}
                </Button>
              </Tooltip>
            }

            <Button
              onClick={publishProducts}
              margin="0 10px 0 0"
              bgColor={Main.gray}
              borderColor={Main.gray}
              disabled={selectedRows.length === 0}
              permissionCode={typeConfig.permissions.publish}
            >
              {PRODUCT_CONSTANTS.PUBLISH}
            </Button>

            <Button
              onClick={() => {
                if (!validateProductCreation(selectedVendor, selectedBusinessUnit, featureFlags?.businessUnit?.enabled!)) {
                  return;
                }
                navigate(typeConfig.newUrl);
              }}
              margin="0 10px 0 0"
              bgColor="#FC780B"
              borderColor={Main.brandDark}
              permissionCode={typeConfig.permissions.createDelete}
            >
              {typeConfig.newButtonLabel}
            </Button>
            {typeConfig.aiEnabled && selectedVendor?.configuration?.aiEnabled &&
              <Button
                onClick={() => {
                  if (!validateProductCreation(selectedVendor, selectedBusinessUnit, featureFlags?.businessUnit?.enabled!)) {
                    return;
                  }
                  setShowImportModal(true);
                }}
                margin="0 10px 0 0"
                bgColor="#FC780B"
                borderColor={Main.brandDark}
                permissionCode={typeConfig.permissions.createDelete}
                leftIcon={() => <ProductBrochureIcon name='ProductBrochure' />}
              >
                {PRODUCT_CONSTANTS.NEW_PRODUCT_AI}
                <StyledBadge
                  badgeContent='Beta'
                  color='#FFFFFF'
                  bgColor='#FF8E33 !important'
                  borderRadius='10px'
                />
              </Button>
            }
          </>}
        </Grid>
      </GridPadding>

      <ProductListingFilters
        selectedVendorId={selectedVendor ? selectedVendor.id : undefined}
        vendorsList={vendorsList}
        filters={filters}
        filterUpdate={handleFilterUpdate}
        updateColumnSettings={setColumns}
        productColumns={columns}
        loading={loading}
        selectedBrands={selectedBrands}
        isBrandUser={isBrandUser}
        hideBrandFilter={typeConfig.hideBrandFilter}
      />
      <GridMargin sx={{ width: '100%' }}>
        <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{ marginTop: '20px', width: '100%' }}>
          <ProductListingTable
            productColumns={columns}
            filters={filters}
            searchTerm={searchTerm}
            selectedVendorId={selectedVendor ? selectedVendor.id : undefined}
            setShowDeleteModal={setShowDeleteModal}
            setShowUpdateModal={setShowUpdateModal}
            setShowPublishModal={setShowPublishModal}
            setSelectedRows={setSelectedRows}
            showDeleteModal={showDeleteModal}
            showUpdateModal={showUpdateModal}
            showPublishModal={showPublishModal}
            selectedRows={selectedRows}
            reload={reload}
            setReload={setReload}
            setLoading={setLoading}
            loading={loading}
            productType={productType}
          />

          {!typeConfig.hideActionButtons && <FlexboxContainer margin='24px 0px'>
            <Button
              borderColor='#C82135'
              background='#C82135'
              onClick={onDelete}
              disabled={!selectedRows.length}
              permissionCode={typeConfig.permissions.createDelete}
            >
              {PRODUCT_CONSTANTS.DELETE_SELECTED}
            </Button>
          </FlexboxContainer>}
        </Grid>
      </GridMargin>

      <ImportModal
        show={showImportModal}
        onClose={handleCloseImportModal}
        isUploading={isUploading}
        setIsUploading={setIsUploading}
        progressSocket={`product_import_progress_${selectedVendor?.id}`}
        importResultSocket={`product_import_completion_${selectedVendor?.id}`}
        uploadData={handleUpload}
        heading='Upload Product Brochure - AI powered'
        step={vendorImportStates[selectedVendor?.id]?.step ?? STEPS.UPLOADING}
        setStep={(step) => updateVendorImportState(selectedVendor!.id, { step })}
        uploadingStatus={vendorImportStates[selectedVendor?.id]?.uploadingStatus ?? null}
        setUploadingStatus={(status) => updateVendorImportState(selectedVendor!.id, { uploadingStatus: status })}
        allowAnyFormat
        isGenAI
        setExcelData={setFileData}
        onViewResult={handleViewResult}
        isSuccess={!!genAIProductId}
        vendorId={selectedVendor?.id}
        token={token || undefined}
      />
    </ContainerGrid>
  );
};

export default ProductListV2;
